/**
 * Starting the offline machinery.
 *
 * Runs after the first render, never before it. Opening IndexedDB and registering a
 * worker are both things an operator should not be made to wait for while standing at
 * a truck; the screen paints first and the plumbing catches up.
 *
 * Every step here is allowed to fail without taking the app down. A device policy can
 * block storage, a private window can block workers, and an old browser can lack
 * both. In each case the app runs online-only: worse, but working.
 */

import { httpTransport } from '../data/http-transport.ts';
import { openInspectDb } from '../data/idb-schema.ts';
import { drainOnce } from '../data/sync.ts';
import { startSyncEngine } from '../data/sync-triggers.ts';
import { registerServiceWorker, requestPersistentStorage } from './register-sw.ts';

export async function startOfflineSupport(): Promise<void> {
  try {
    const db = await openInspectDb();
    const transport = httpTransport();

    startSyncEngine(db, transport);

    // The worker cannot reach IndexedDB helpers itself, so a background sync on
    // Android arrives here as a message and this page does the draining.
    registerServiceWorker(() => {
      void drainOnce(db, transport);
    });

    void requestPersistentStorage();
  } catch {
    // Online-only. An operator with no storage can still complete an inspection; it
    // simply has to reach the server while they are standing there.
  }
}
